import { Heading, Input, Link, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";

import { Button } from "../components/button";
import { Field } from "../components/field";
import LoginShell from "../components/login-shell";
import { PasswordInput } from "../components/password-input";
import { toaster } from "../components/toaster";
import { useAppDispatch, useAppSelector } from "../hooks";
import { setReturnLocation, signUp } from "../store/authSlice";

interface SignupForm {
  username: string;
  password: string;
}

export default function SignUpPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { returnLocation } = useAppSelector((s) => s.auth);

  const [isLoading, setIsLoading] = React.useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupForm>();
  const onSubmit = handleSubmit(async ({ username, password }) => {
    setIsLoading(true);
    try {
      await dispatch(signUp({ username, password })).unwrap();
      dispatch(setReturnLocation(null));
      navigate(returnLocation || "/");
    } catch (e) {
      toaster.create({
        type: "error",
        title: "Error",
        description: e.message,
      });
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <LoginShell>
      <Stack gap={{ base: "2", md: "3" }} textAlign="center">
        <Heading color="fg.accent" size={{ base: "2xl", md: "3xl" }}>
          Sign Up
        </Heading>
      </Stack>
      <form onSubmit={onSubmit}>
        <Stack gap="6">
          <Stack gap="5">
            <Field
              label="Email"
              invalid={!!errors.username}
              errorText={errors.username?.message}
            >
              <Input
                type="email"
                placeholder="johndoe@example.com"
                {...register("username", {
                  required: "Please provide an e-mail address.",
                })}
              />
            </Field>
            <Field
              label="Password"
              invalid={!!errors.password}
              errorText={errors.password?.message}
            >
              <PasswordInput
                {...register("password", {
                  required: "Please provide a password.",
                })}
              />
            </Field>
          </Stack>
          <Stack gap="4">
            <Button loading={isLoading} type="submit">
              Sign Up
            </Button>
          </Stack>

          <Text textStyle="sm" color="fg.muted" textAlign="center">
            Already have an account?{" "}
            <Link asChild variant="underline">
              <RouterLink to="/login">Sign In</RouterLink>
            </Link>
          </Text>
        </Stack>
      </form>
    </LoginShell>
  );
}
